import React, { useState, useEffect } from 'react';
import { Save, Facebook, Instagram, Twitter } from 'lucide-react';
import { SiteSettingsFormData } from '@/types/settings';
import { settingsApi } from '@/services/settingsApi';
import toast from 'react-hot-toast';

export const Settings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SiteSettingsFormData>({
    appName: '',
    contactEmail: '',
    contactPhone: '',
    deliveryRegion: '',
    minOrderAmount: 0,
    deliveryDescription: '',
    creditCardPaymentBypassEnabled: false,
    bankTransferEnabled: false,
    invoiceAutomationEnabled: false,
    invoiceDelayMinutes: 60,
    invoiceRetryLimit: 3,
    activeInvoiceProviderType: 'faturaport',
    faturaportCompanyCode: '',
    faturaportClientId: '',
    faturaportClientSecret: '',
    smsProvider: 'none',
    smsApiKey: '',
    facebookUrl: '',
    instagramUrl: '',
    twitterUrl: '',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const data = await settingsApi.getSettings();
      setSettings({
        appName: data.appName,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        deliveryRegion: data.deliveryRegion,
        minOrderAmount: data.minOrderAmount,
        deliveryDescription: data.deliveryDescription,
        creditCardPaymentBypassEnabled: data.creditCardPaymentBypassEnabled || false,
        bankTransferEnabled: data.bankTransferEnabled || false,
        invoiceAutomationEnabled: data.invoiceAutomationEnabled || false,
        invoiceDelayMinutes: data.invoiceDelayMinutes || 60,
        invoiceRetryLimit: data.invoiceRetryLimit || 3,
        activeInvoiceProviderType: data.activeInvoiceProviderType || 'faturaport',
        faturaportCompanyCode: data.faturaportCompanyCode || '',
        faturaportClientId: data.faturaportClientId || '',
        faturaportClientSecret: data.faturaportClientSecret || '',

        smsProvider: data.smsProvider || 'none',
        smsApiKey: data.smsApiKey || '',
        facebookUrl: data.facebookUrl || '',
        instagramUrl: data.instagramUrl || '',
        twitterUrl: data.twitterUrl || '',
      });
    } catch (error: any) {
      console.error('Ayarlar yüklenirken hata:', error);
      toast.error(error.message || 'Ayarlar yüklenemedi');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsApi.updateSettings(settings);
      toast.success('✅ Ayarlar başarıyla kaydedildi!');
      
      // Background'da ayarları yeniden yükle (loading spinner göstermez)
      try {
        await loadSettings(false);
      } catch (err) {
        console.warn('Ayarları yeniden yüklerken hata (sessiz):', err);
      }
    } catch (error: any) {
      console.error('Kaydetme hatası:', error);
      toast.error(error.message || 'Ayarlar kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof SiteSettingsFormData, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin text-4xl">⏳</div>
          <p className="text-gray-600 dark:text-gray-400">Ayarlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-dark dark:text-light">Ayarlar</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Sistem ayarlarını yönetin</p>
      </div>

      {/* Settings Sections */}
      <div className="grid gap-6">
        {/* General Settings */}
        <div className="card">
          <h2 className="text-lg font-semibold text-dark dark:text-light mb-4">Genel Ayarlar</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Uygulama Adı</label>
              <input
                type="text"
                className="input-field"
                value={settings.appName}
                onChange={(e) => handleChange('appName', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">İletişim Email</label>
              <input
                type="email"
                className="input-field"
                value={settings.contactEmail}
                onChange={(e) => handleChange('contactEmail', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">İletişim Telefonu</label>
              <input
                type="tel"
                className="input-field"
                value={settings.contactPhone}
                onChange={(e) => handleChange('contactPhone', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Business Settings */}
        <div className="card">
          <h2 className="text-lg font-semibold text-dark dark:text-light mb-4">İşletme Ayarları</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Teslimat Bölgesi</label>
                <input
                  type="text"
                  className="input-field"
                  value={settings.deliveryRegion}
                  onChange={(e) => handleChange('deliveryRegion', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Minimum Sipariş Tutarı</label>
                <input
                  type="number"
                  className="input-field"
                  value={settings.minOrderAmount}
                  onChange={(e) => handleChange('minOrderAmount', parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Teslimat Açıklaması</label>
              <textarea
                className="input-field h-24 resize-none"
                value={settings.deliveryDescription}
                onChange={(e) => handleChange('deliveryDescription', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Payment Settings */}
        <div className="card">
          <h2 className="text-lg font-semibold text-dark dark:text-light mb-4">Ödeme Ayarları</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Sanal POS tanımlamaları ve taksit ayarları için POS Yönetimi sayfasını kullanın.
          </p>
          <label className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 mb-4 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={settings.creditCardPaymentBypassEnabled}
              onChange={(e) => handleChange('creditCardPaymentBypassEnabled', e.target.checked)}
            />
            <span>
              <span className="block text-sm font-medium text-dark">Kredi kartı ödemeyi backend’de bypass et</span>
              <span className="block text-sm text-gray-600 mt-1">
                Açıkken web ve mobilde kredi kartı ödeme isteği POS’a gitmez; sipariş doğrudan ödeme alınmış olarak işaretlenir.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 mb-4 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={settings.bankTransferEnabled}
              onChange={(e) => handleChange('bankTransferEnabled', e.target.checked)}
            />
            <span>
              <span className="block text-sm font-medium text-dark">Havale / EFT ile ödeme aktif</span>
              <span className="block text-sm text-gray-600 mt-1">
                Açıkken web ve mobilde müşteri havale ile ödeme yapabilir; sipariş "havale bekleniyor" durumunda oluşur ve admin manuel onaylar.
              </span>
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            <a
              href="/admin/payment-providers"
              className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              POS Yönetimi →
            </a>
            <a
              href="/admin/bank-accounts"
              className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
            >
              Banka Hesapları →
            </a>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-dark dark:text-light mb-4">E-Fatura Ayarları</h2>
          <div className="space-y-4">
            <label className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={settings.invoiceAutomationEnabled}
                onChange={(e) => handleChange('invoiceAutomationEnabled', e.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-dark">Otomatik fatura job aktif</span>
                <span className="block text-sm text-gray-600 mt-1">
                  Saatlik job yalnızca fatura kesilmeye hazır ve bekleme süresi dolmuş siparişleri işler.
                </span>
              </span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Bekleme Süresi (dakika)</label>
                <input
                  type="number"
                  min="0"
                  className="input-field"
                  value={settings.invoiceDelayMinutes}
                  onChange={(e) => handleChange('invoiceDelayMinutes', parseInt(e.target.value, 10) || 0)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Retry Limiti</label>
                <input
                  type="number"
                  min="1"
                  className="input-field"
                  value={settings.invoiceRetryLimit}
                  onChange={(e) => handleChange('invoiceRetryLimit', parseInt(e.target.value, 10) || 1)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Aktif Fatura Sağlayıcısı</label>
              <select
                className="input-field"
                value={settings.activeInvoiceProviderType}
                onChange={(e) => handleChange('activeInvoiceProviderType', e.target.value as 'none' | 'faturaport')}
              >
                <option value="faturaport">FaturaPort</option>
                <option value="none">Kapalı</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">FaturaPort Firma Kodu</label>
                <input
                  type="text"
                  className="input-field"
                  value={settings.faturaportCompanyCode || ''}
                  onChange={(e) => handleChange('faturaportCompanyCode', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">FaturaPort Client ID</label>
                <input
                  type="text"
                  className="input-field"
                  value={settings.faturaportClientId || ''}
                  onChange={(e) => handleChange('faturaportClientId', e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">FaturaPort Client Secret</label>
              <input
                type="password"
                className="input-field"
                value={settings.faturaportClientSecret || ''}
                onChange={(e) => handleChange('faturaportClientSecret', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* SMS Settings */}
        <div className="card">
          <h2 className="text-lg font-semibold text-dark dark:text-light mb-4">SMS Ayarları</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">SMS API Provider</label>
              <select
                className="input-field"
                value={settings.smsProvider || 'none'}
                onChange={(e) => handleChange('smsProvider', e.target.value as any)}
              >
                <option value="none">Seçiniz</option>
                <option value="netgsm">Netgsm</option>
                <option value="bulutfon">Bulutfon</option>
                <option value="twilio">Twilio</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">API Key</label>
              <input
                type="password"
                className="input-field"
                placeholder="API Key"
                value={settings.smsApiKey || ''}
                onChange={(e) => handleChange('smsApiKey', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Social Media Settings */}
        <div className="card">
          <h2 className="text-lg font-semibold text-dark dark:text-light mb-4">Sosyal Medya</h2>
          <div className="space-y-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-1">
                <Facebook size={16} className="text-blue-600" /> Facebook
              </label>
              <input
                type="url"
                className="input-field"
                placeholder="https://facebook.com/tazebijest"
                value={settings.facebookUrl || ''}
                onChange={(e) => handleChange('facebookUrl', e.target.value)}
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-1">
                <Instagram size={16} className="text-pink-600" /> Instagram
              </label>
              <input
                type="url"
                className="input-field"
                placeholder="https://instagram.com/tazebijest"
                value={settings.instagramUrl || ''}
                onChange={(e) => handleChange('instagramUrl', e.target.value)}
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-1">
                <Twitter size={16} className="text-sky-500" /> Twitter / X
              </label>
              <input
                type="url"
                className="input-field"
                placeholder="https://x.com/tazebijest"
                value={settings.twitterUrl || ''}
                onChange={(e) => handleChange('twitterUrl', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center gap-2 w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={20} />
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>
    </div>
  );
};
